<template>
  <div :class="['product-item', `view-${props.viewType}`, {'no-control': !props.useControl}]" v-if="props.item.id">
    <!-- 상품 섬네일 s -->
    <div class="thumb-wrap" :class="[{'sold-out': props.item.status === 'soldout'}, {'temp-sold-out': props.item.status === 'tempSoldout'}, {'not-available': props.item.status === 'notAvailable'}, {'sales-end': props.item.status === 'salesEnd'}, {'exh-discont': props.item.status === 'exhDiscont'}, {'exh-before': props.item.status === 'exhBefore'}]">
      <NuxtLink :to="props.item.link" class="item-link">
        <div class="item-thumb">
          <img :src="props.item.thumb" :alt="props.item.alt" class="thumb-img">
        </div>
        <div v-if="props.useControl && props.item.rank" class="product-rank">
          <span class="rank-text">{{ props.item.rank }}</span>
        </div>
      </NuxtLink>
      <div v-if="props.useControl && props.item.status === ''" class="product-control">
        <div class="button-box">
          <button type="button" @click="cartInsertCheck" class="btn-func"><img src="@/assets/images/pc/icon-hcon/icon-cart-white.svg" alt="장바구니 담기"/></button>
          <button type="button" @click="quoteInsertCheck" class="btn-func"><img src="@/assets/images/pc/icon-hcon/icon-calc-white.svg" alt="견적 담기"/></button>
          <button type="button" @click="buyNow" class="btn-func"><img src="@/assets/images/pc/icon-hcon/icon-card-white.svg" alt="바로구매"/></button>
        </div>
      </div>
    </div>
    <!-- 상품 섬네일 e -->
    <!-- 상품 정보 s -->
    <div v-if="!props.hideInfo" class="item-info-wrap">
      <div v-if="props.item.status === 'soldout' || props.item.status === 'tempSoldout' || props.item.status === 'notAvailable' || props.item.status === 'salesEnd' || props.item.status === 'exhDiscont'">
      <NuxtLink class="basic-info">
        <p class="company">{{ props.item.company }}</p>
        <p class="item-name multi-ellips">{{ item.name }}</p>
      </NuxtLink>
    </div>
    <div v-else>
      <NuxtLink :to="props.item.link" class="basic-info">
        <p class="company">{{ props.item.company }}</p>
        <p class="item-name multi-ellips">{{ item.name }}</p>
      </NuxtLink>
    </div>
      <div class="price-wrap">
        <template v-if="props.item.status === 'salesEnd'">
          <strong class="text-red">현재 판매가 종료된 상품입니다.</strong>
        </template>
        <template v-else-if="props.item.status === 'exhDiscont'">
          <strong class="text-red">판매 종료되어 페이지를 볼 수 없습니다.</strong>
        </template>
        <template v-else-if="props.item.status === 'notAvailable'">
          <strong class="text-red">현재 판매가 중지된 상품입니다.</strong>
        </template>
        <template v-else>
          <template v-if="hasDiscount">
            <span class="total-price">{{ HconCommonUtils.format.expComma(props.item.originalPrice) }}원</span>
            <div class="price-box">
              <span class="discount">{{ props.item.discount }}%</span>
              <!-- <strong class="price">{{ props.item.isHighest ? `~ ${HconCommonUtils.format.expComma(props.item.price)}원` : `${HconCommonUtils.format.expComma(props.item.price)}원 ~` }}</strong> -->
              <strong class="price">{{ `${HconCommonUtils.format.expComma(props.item.price)}원 ~` }}</strong>
            </div>
          </template>
          <template v-else>
            <span class="total-price only">{{ HconCommonUtils.format.expComma(props.item.originalPrice) }}원</span>
          </template>
        </template>
      </div>
      <div v-if="props.item.badge && props.item.badge.length > 0" class="badge-wrap">
        <span v-for="badge, index in props.item.badge" :key="index" :class="['badge', `${badge.key}`]">{{ badge.text }}</span>
      </div>
    </div>
    <!-- 상품 정보 e -->
    <!-- 상품 뱃지 s -->
    <div v-if="props.item.badge && props.item.badge.length > 0" class="badge-wrap">
      <span v-for="badge, index in props.item.badge" :key="index" :class="['badge', `${badge.key}`]">{{ badge.text }}</span>
    </div>
    <!-- 상품 뱃지 e -->
  </div>
</template>
<script lang="ts" setup>
import { defineProps } from 'vue';
import { usePopup } from '@/composables/hconUsePopup';
import { ORDER_CODE, ESTIMATE_CODE } from '~/constants/hconMessageConstants';

const route = useRoute();

const router = useRouter();

const fetchBuilder = useHconFetchBuilder();
const { bsktCnt, tmpOrdCnt, setBsktCnt, setTmpOrdCnt, refreshCnt } = useHeader()

type ViewType = 'column' | 'row' // 세로형, 가로형
type ProductStatus = '' | 'soldout' | 'tempSoldout' | 'notAvailable' | 'salesEnd' | 'exhDiscont' | 'exhBefore' // 판매 상태: 일반, 품절, 일시품절, 판매중지, 판매종료, 전시중단, 전시 전

interface ProductItemBadge {
  key: string
  text: string
}

export interface ProductItemType {
  isCashable? : boolean
  hcBrndCd?: string 
  hcDspGdsCd?: string 
  hcCtgrCd? : string 
  id?: number | string
  company: string
  name: string
  link: string
  rank?: number
  discount?: number | string
  price?: number
  originalPrice: number
  thumb: string
  alt: string
  status: ProductStatus
  badge?: ProductItemBadge[]
  isHighest: boolean // 전시 상품이 최고가인 경우
}

interface Props {
  viewType: ViewType
  hideInfo: boolean,
  useControl: boolean,
  item: ProductItemType
}

const props = withDefaults(defineProps<Props>(), {
  viewType: 'column',
  hideInfo: false,
  useControl: true,
  status: '',
  discount: 0,
  originalPrice: 0,
});

// 할인율이 있을 경우 보임
const hasDiscount = computed(() => {
  const item = props.item

  if (item.discount === undefined || item.discount === null || item.discount === 0) {
    return false
  }

  const discountNum = Number(item.discount)

  return !isNaN(discountNum) && discountNum > 0
})

// Control 팝업
const {
  showAlert,
  showConfirm,
  openLoginPopup,
  isLoggedIn
} = usePopup()

const loginConfirm = async () => {

const result = await showConfirm({
  title: "로그인 확인",
  message: "로그인 후 이용 가능한 기능입니다.<br/>로그인하시겠습니까?",
  confirmText: "로그인하기"
})
if(result){
  openLoginPopup();
}
}

const cartInsertCheck = () => {
  if(!isLoggedIn.value){
    loginConfirm()
  }else{
      fetchBuilder.post<AjaxResult<any>>('/api/v1/order/shopping-basket/create')
      .body({
        hcDspGdsCd : props.item.hcDspGdsCd,
        hcGdsCd : props.item.id
      })
      .progressMessage("잠시만 기다려주세요...")
      .preventErrorNotify()
      .build().execute()
      .then(async (result) => {

        if(result.resultCode == '200') {
          refreshCnt();

          const basketAlert = await showConfirm({
            title: "장바구니 담기 완료",
            message: HconCommonUtils.format.message(ORDER_CODE.ALT_HCON_9053),
            confirmText: "장바구니로 이동"
          })

          if(basketAlert) {
            navigateTo({path: '/pc/order/basket'}, {replace: false});
          }
        }
      })
      .catch(async (error) => {    // 응답코드가 200이 아닌경우
          if(error.resultCode != '200') {
            if (error.resultCode == '29052'){ 
              showAlert({
                title: "장바구니 담기 실패",
                message: HconCommonUtils.format.message(ORDER_CODE.ALT_HCON_9052),
              })
              return false;
            } else if (error.resultCode == '29054'){ 
              const basketAlert = await showConfirm({
                title: "장바구니 제한 수량 초과",
                message: HconCommonUtils.format.message(ORDER_CODE.ALT_HCON_9054),
                 confirmText: "장바구니로 이동"
              })

              if(basketAlert) {
                return navigateTo({path: '/pc/order/basket'}, {replace: false});
              } else {
                return false;
              }

            } else {
              showAlert({
                title: "장바구니 담기 실패",
                message: error.resultMessage,
              })
              return false;
            }
          }
        })
  }
  
}

const emit = defineEmits<{ (e: 'openPopup', message:string): void }>()

const quoteInsertCheck = () => {
  if(!isLoggedIn.value){
    loginConfirm()
  }else{
        fetchBuilder.post<AjaxResult<any>>('/api/v1/order/estimate/items')
    .body({
      gdsList: [{
        hcDspGdsCd : props.item.hcDspGdsCd,
        hcGdsCd : props.item.id
        }]
      })
    .progressMessage("잠시만 기다려주세요...")
    .preventErrorNotify()
    .build()
    .execute()
    .then(async (result) => {
      if(result.resultCode != '200') {
        showAlert({
          title: '알림',
          message: result.resultMessage,
          confirmText: '확인'
        })
      } else {
        const resultMsg = result.data.message
        if(resultMsg === "QuantityOver") { // 수량 초과
          const resultConfirm = await showConfirm({
            title: "견적 담기 제한 수량 초과",
            message: HconCommonUtils.format.message(ESTIMATE_CODE.ALT_HCON_9059),
            confirmText: "담긴 상품 불러오기"
          })
        
          if(resultConfirm){
            emit('openPopup', 'QuantityOver')
          } else {
            return false;
          }
        } else if(resultMsg === "DuplicateProduct") { // 중복 
          const resultConfirm = await showConfirm({
            title: "견적 담기 중복 오류",
            message: HconCommonUtils.format.message(ESTIMATE_CODE.ALT_HCON_9057),
            confirmText: "온라인 견적서로 이동"
          })

          if(resultConfirm){
            router.push({
            path: '/pc/order/estimate'
            // state: {}
            })
          } else {
            return false;
          }
        } else { // 정상 처리
          refreshCnt();
          const resultConfirm = await showConfirm({
            title: "견적 담기 완료",
            message: HconCommonUtils.format.message(ESTIMATE_CODE.ALT_HCON_9058),
            confirmText: "온라인 견적서로 이동"
          })

          if(resultConfirm){
            router.push({
            path: '/pc/order/estimate' // "담은 상품 불러오기 팝업 노출 - 현재 페이지 없음"
            // state: {}
            })
          } else {
            return false;
          }
        }
      }        
    })
    .catch((error) => {    // 응답코드가 200이 아닌경우
        if(error.resultCode != '200') {
          if(error.resultCode == '29209') {
            showAlert({
              title: '충천 취소 오류',
              message: error.resultMessage /*이전에 사용한 비밀번호는 다시 사용할 수 없습니다.*/
            })
          }
          else {
            showAlert(error.resultMessage)
          }
        }
      })
      }
}

const buyNow = () => {
  if(!isLoggedIn.value){
    loginConfirm()
  }else{
    if(props.item.isCashable) {
      const nowTime = HconCommonUtils.format.todayDateTime()?.split(' ')[1].replace(':','').substring(0,4)
      
      if (Number(nowTime) < 900 || Number(nowTime) > 1800 ) {
        showAlert({
            title: "판매 불가 알림",
            message: HconCommonUtils.format.message(ORDER_CODE.ALT_HCON_9218),
          })
          return false;
      }

      fetchBuilder.get<AjaxResult<any>>('/api/v1/product/detail/search-holyday/'+HconCommonUtils.format.todayDateKor())
      .progressMessage("잠시만 기다려주세요...")
      .preventErrorNotify()
      .build()
      .execute()
      .then(async (result) => {
        if(result.data > 0) {
            showAlert({
              title: "판매 불가 알림",
              message: HconCommonUtils.format.message(ORDER_CODE.ALT_HCON_9218),
            })
          return false;
          }
        })
    }

      router.push({
            path: '/pc/order/application-form',
            state: {
              prevPage: 'A',
              originalUrl: route.fullPath,
              gdsList: JSON.parse(JSON.stringify([{
                hcDspGdsCd : props.item.hcDspGdsCd,
                hcGdsCd : props.item.id
              }]))
            }
          }) 
        }
}

</script>
<style lang="scss" scoped>
</style>